<template>
  <picture
    :key="pictureKey"
    class="responsive-image size-full"
    :class="fit === 'scale' && 'overflow-hidden'"
  >
    <source
      v-for="source in sources"
      :key="source?.media"
      :media="source?.media"
      type="image/jpeg"
      :srcset="source?.srcset.join(', ')"
    />
    <img
      v-if="fallback"
      width="380"
      height="285"
      :alt="alt"
      :title="title"
      :loading="noLazyLoading ? 'eager' : 'lazy'"
      :src="fallback"
      :class="imageClass"
      :style="{ objectPosition }"
      :draggable="noDraggable ? 'false' : undefined"
    />
  </picture>
</template>

<script lang="ts" setup>
import type { ResponsiveStyleName } from '~/helpers/responsiveStyles'
import {
  responsiveStylesMap,
  BREAKPOINTS,
  breakpointOrder,
} from '~/helpers/responsiveStyles'
import {
  cantoCropAlignmentObjectPosition,
  DEFAULT_CANTO_CROP_ALIGNMENT,
  getCantoImageStyle,
  resolveCantoCropAlignment,
  type CantoCropAlignment,
} from '~/helpers/canto'
import type { ClassProp } from '~/types/vue'

interface Source {
  media?: string
  srcset: string[]
  breakpoint: string
}

const props = withDefaults(
  defineProps<{
    alt?: string
    title?: string
    renderUrl?: string
    responsiveStyle?: ResponsiveStyleName
    noLazyLoading?: boolean
    imageClass?: ClassProp
    noDraggable?: boolean
    /**
     * Optional aspect ratio (width / height) that overrides the crop of the
     * responsive style. Useful to render e.g. a portrait crop from a landscape
     * responsive style, keeping the source width and recomputing the height.
     */
    aspectRatio?: number
    /**
     * Canto smart-crop placement (`Ro:`). Defaults to center. Stored per
     * usage (e.g. Blokkli block), not on the Canto asset.
     */
    cropAlignment?: CantoCropAlignment
    /**
     * `crop`: MDC smart-crop to the style size.
     * `scale`: keep original aspect ratio so CSS object-position can pan.
     */
    fit?: 'crop' | 'scale'
  }>(),
  {
    alt: '',
    title: '',
    renderUrl: '',
    responsiveStyle: 'column_full',
    noLazyLoading: false,
    imageClass: '',
    noDraggable: false,
    aspectRatio: undefined,
    cropAlignment: DEFAULT_CANTO_CROP_ALIGNMENT,
    fit: 'crop',
  },
)

const objectPosition = computed(() =>
  cantoCropAlignmentObjectPosition(props.cropAlignment),
)

const pictureKey = computed(
  () =>
    `${props.renderUrl}-${props.cropAlignment}-${props.fit}-${props.responsiveStyle}-${props.aspectRatio ?? ''}`,
)

const responsiveStyleItem = computed(() =>
  responsiveStylesMap.get(props.responsiveStyle),
)

// Build Canto options for a given responsive style id, optionally overriding the
// crop height so it matches the requested aspect ratio.
function cantoOptions(imageStyle: string) {
  const orientation = resolveCantoCropAlignment(props.cropAlignment)
  const width = Number(imageStyle.split('_')[0])
  if (!props.aspectRatio || Number.isNaN(width)) {
    return { imageStyle, orientation, fit: props.fit }
  }
  return {
    width,
    height: Math.round(width / props.aspectRatio),
    orientation,
    fit: props.fit,
  }
}

const fallback = computed(() => {
  if (!props.renderUrl) return ''
  const imageStyle = responsiveStyleItem.value?.styles?.[0]?.id || '-S300x225'
  return getCantoImageStyle(props.renderUrl, cantoOptions(imageStyle)).url
})

const sources = computed(() => {
  if (!props.renderUrl) return []
  const sources: Record<string, Source> = {}
  responsiveStyleItem.value?.styles?.forEach((imageStyle) => {
    if (!sources[imageStyle.viewport]) {
      sources[imageStyle.viewport] = {
        media: BREAKPOINTS[imageStyle.viewport],
        srcset: [],
        breakpoint: imageStyle.viewport,
      }
    }
    sources[imageStyle.viewport]?.srcset.push(
      `${getCantoImageStyle(props.renderUrl, cantoOptions(imageStyle.id)).url} ${imageStyle.density}`,
    )
  })
  return Object.values(sources).sort(
    (a, b) =>
      breakpointOrder.indexOf(a.breakpoint) -
      breakpointOrder.indexOf(b.breakpoint),
  )
})
</script>

<style lang="postcss">
.responsive-image {
  @apply block;
  img {
    @apply block w-full;
  }
}
</style>
